import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  MedicalServices as MedicalServicesIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  PictureAsPdf as PreviewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { formatCurrencySync } from '../../utils/currencyUtils';
import ToothSelector from '../../components/treatments/ToothSelector';
import { downloadTreatmentPlanPDF, openTreatmentPlanPDF } from '../../utils/pdfTreatmentPlan';
import useClinicScopeRefresh from '../../hooks/useClinicScopeRefresh';
import {
  SESSION_EXPIRED_MESSAGE,
  formatFailedActionMessage,
  formatSuccessActionMessage,
} from '../../utils/uiMessages';
import { getApiErrorMessage } from '../../utils/errorUtils';

const TreatmentPlans = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [patients, setPatients] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [toothSelectorOpen, setToothSelectorOpen] = useState(false);
  const [selectedTreatmentForTeeth, setSelectedTreatmentForTeeth] = useState(null);

  // Form state for new/edit plan
  const [planForm, setPlanForm] = useState({
    patientId: '',
    name: '',
    description: '',
    treatments: [], // Array of { treatment, teeth, quantity, cost, duration, discount }
    totalCost: 0,
    estimatedDuration: 0,
    discountPercent: 0, // plan-level discount %
    status: 'draft'
  });

  // Handle query parameters separately after data is loaded
  useEffect(() => {
    const patientId = searchParams.get('patientId');
    const editPlanId = searchParams.get('edit');
    const viewPlanId = searchParams.get('view');

    if (patientId && patients.length > 0) {
      // Set patient in form when patientId is provided
      setPlanForm(prev => ({
        ...prev,
        patientId: patientId
      }));
      // Open dialog if patient is selected
      if (!planDialogOpen) {
        setPlanDialogOpen(true);
      }
    }

    if (editPlanId && treatmentPlans.length > 0) {
      // Find and edit the plan when edit parameter is provided
      const planToEdit = treatmentPlans.find(p => (p._id || p.id) === editPlanId);
      if (planToEdit) {
        handleEditPlan(planToEdit);
        // Clear the edit parameter from URL
        searchParams.delete('edit');
        setSearchParams(searchParams);
      }
    }

    if (viewPlanId && treatmentPlans.length > 0) {
      // Find and view the plan when view parameter is provided
      const planToView = treatmentPlans.find(p => (p._id || p.id) === viewPlanId);
      if (planToView) {
        handleViewPlan(planToView);
        // Clear the view parameter from URL
        searchParams.delete('view');
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, patients, treatmentPlans, planDialogOpen, setSearchParams]);

  // Fetch treatment plans
  const fetchTreatmentPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_URL}/treatment-plans`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Transform data to match frontend format
      const plans = (response.data.data || []).map(plan => ({
        id: plan._id,
        _id: plan._id,
        name: plan.name,
        description: plan.description,
        patientId: plan.patient?._id || plan.patient,
        patientName: plan.patient?.name || 'Unknown Patient',
        treatments: (plan.treatments || []).map(t => {
          // Handle both old format (direct treatment ID) and new format (object with treatment, teeth, etc.)
          if (t && t.treatment) {
            return {
              treatment: t.treatment,
              teeth: Array.isArray(t.teeth) ? t.teeth : [],
              quantity: Number(t.quantity) || 1,
              discount: Math.min(100, Math.max(0, Number(t.discount) || 0)),
              cost: Number(t.cost) || 0,
              duration: Number(t.duration) || 0
            };
          } else if (t && (t._id || typeof t === 'object')) {
            return {
              treatment: t._id ? t : t,
              teeth: [],
              quantity: 1,
              discount: Math.min(100, Math.max(0, Number(t.discount) || 0)),
              cost: Number(t.price) || 0,
              duration: Number(t.duration) || 0
            };
          } else {
            // Fallback - should not happen
            console.warn('Unexpected treatment format:', t);
            return {
              treatment: t,
              teeth: [],
              quantity: 1,
              cost: 0,
              duration: 0
            };
          }
        }),
        totalCost: plan.totalCost,
        estimatedDuration: plan.estimatedDuration,
        discountPercent: Math.min(100, Math.max(0, Number(plan.discountPercent) || 0)),
        status: plan.status,
        createdAt: plan.createdAt
      }));
      
      setTreatmentPlans(plans);
      
    } catch (err) {
      console.error('Error fetching treatment plans:', err);
      setError(formatFailedActionMessage('load', 'treatment plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_URL}/patients`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setPatients(response.data.data);
    } catch (err) {
      console.error('Error fetching patients:', err);
      toast.error(formatFailedActionMessage('load', 'patients'));
    }
  }, []);

  // Fetch treatments
  const fetchTreatments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await axios.get(`${API_URL}/treatments`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTreatments(response.data.data);
    } catch (err) {
      console.error('Error fetching treatments:', err);
      toast.error(formatFailedActionMessage('load', 'treatments'));
    }
  }, []);

  useEffect(() => {
    fetchTreatmentPlans();
    fetchPatients();
    fetchTreatments();
    // Load currency on mount (force refresh to get latest currency)
    const loadCurrency = async () => {
      const { getOrganizationCurrency } = await import('../../utils/currencyUtils');
      await getOrganizationCurrency(true); // Force refresh to get latest currency
    };
    loadCurrency();
  }, [fetchPatients, fetchTreatmentPlans, fetchTreatments]);

  const refreshScopeData = useCallback(() => {
    fetchTreatmentPlans();
    fetchPatients();
    fetchTreatments();
  }, [fetchPatients, fetchTreatmentPlans, fetchTreatments]);
  useClinicScopeRefresh(refreshScopeData);

  // Format currency using organization settings
  const formatCurrency = (amount) => {
    return formatCurrencySync(amount);
  };

  // Get currency symbol for display (commented out - not currently used)
  // const currencySymbol = getCurrencySymbol();

  // Handle form input changes
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setPlanForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle adding treatment to plan - opens tooth selector
  const handleAddTreatment = (treatment) => {
    setSelectedTreatmentForTeeth(treatment);
    setToothSelectorOpen(true);
  };

  // Subtotal = sum of line costs (after per-procedure discount). Total = subtotal after plan discount.
  const getSubtotalAndTotal = (treatments, discountPercent) => {
    const subtotal = treatments.reduce((sum, t) => {
      const cost = Number(t.cost) || 0;
      return sum + cost;
    }, 0);
    const planDiscountAmount = subtotal * (Number(discountPercent) || 0) / 100;
    const total = Math.round((subtotal - planDiscountAmount) * 100) / 100;
    return { subtotal, planDiscountAmount, total };
  };

  // Handle tooth selection confirmation
  const handleTeethSelected = (teeth) => {
    if (!selectedTreatmentForTeeth) return;

    const quantity = teeth.length || 1; // If no teeth selected, count as 1 treatment
    const unitPrice = selectedTreatmentForTeeth.price ?? 0;
    const treatmentCost = unitPrice * quantity;
    const treatmentDuration = selectedTreatmentForTeeth.duration * quantity;

    // Check if this treatment already exists in the plan
    const existingIndex = planForm.treatments.findIndex(
      t => (t.treatment?._id || t._id) === selectedTreatmentForTeeth._id
    );

    let updatedTreatments;
    let costDifference = 0;
    let durationDifference = 0;

    if (existingIndex >= 0) {
      const existing = planForm.treatments[existingIndex];
      const prevCost = existing.cost ?? (existing.treatment?.price ?? 0) * (existing.quantity || 1);
      costDifference = treatmentCost - prevCost;
      durationDifference = treatmentDuration - (existing.duration || 0);

      updatedTreatments = [...planForm.treatments];
      updatedTreatments[existingIndex] = {
        treatment: selectedTreatmentForTeeth,
        teeth: teeth,
        quantity: quantity,
        discount: 0,
        cost: treatmentCost,
        duration: treatmentDuration
      };
    } else {
      costDifference = treatmentCost;
      durationDifference = treatmentDuration;

      updatedTreatments = [...planForm.treatments, {
        treatment: selectedTreatmentForTeeth,
        teeth: teeth,
        quantity: quantity,
        discount: 0,
        cost: treatmentCost,
        duration: treatmentDuration
      }];
    }

    const { total } = getSubtotalAndTotal(updatedTreatments, planForm.discountPercent);
    setPlanForm(prev => ({
      ...prev,
      treatments: updatedTreatments,
      totalCost: total,
      estimatedDuration: prev.estimatedDuration + durationDifference
    }));

    setSelectedTreatmentForTeeth(null);
    setToothSelectorOpen(false);
  };

  // Handle removing treatment from plan
  const handleRemoveTreatment = (treatmentId) => {
    const treatmentEntry = planForm.treatments.find(t => 
      (t.treatment?._id || t._id) === treatmentId
    );
    
    if (treatmentEntry) {
      const nextTreatments = planForm.treatments.filter(t => 
        (t.treatment?._id || t._id) !== treatmentId
      );
      const { total } = getSubtotalAndTotal(nextTreatments, planForm.discountPercent);
      const newDuration = planForm.estimatedDuration - (treatmentEntry.duration || treatmentEntry.treatment?.duration || 0);
      setPlanForm(prev => ({
        ...prev,
        treatments: nextTreatments,
        totalCost: total,
        estimatedDuration: Math.max(0, newDuration)
      }));
    }
  };

  // Per-procedure discount change: recalc line cost and total
  const handleProcedureDiscountChange = (index, discountPercentVal) => {
    const t = planForm.treatments[index];
    if (!t) return;
    const treatment = t.treatment || t;
    const unitPrice = treatment?.price ?? 0;
    const quantity = Number(t.quantity) || 1;
    const discount = Math.min(100, Math.max(0, Number(discountPercentVal) || 0));
    const cost = Math.round(quantity * unitPrice * (1 - discount / 100) * 100) / 100;

    const updatedTreatments = [...planForm.treatments];
    updatedTreatments[index] = { ...t, discount, cost };

    const { total } = getSubtotalAndTotal(updatedTreatments, planForm.discountPercent);
    setPlanForm(prev => ({
      ...prev,
      treatments: updatedTreatments,
      totalCost: total
    }));
  };

  // Plan-level discount change
  const handlePlanDiscountChange = (e) => {
    const discountPercent = Math.min(100, Math.max(0, Number(e.target.value) || 0));
    const { total } = getSubtotalAndTotal(planForm.treatments, discountPercent);
    setPlanForm(prev => ({
      ...prev,
      discountPercent,
      totalCost: total
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!planForm.patientId) {
        toast.error('Patient is required');
        return;
      }
      if (!planForm.name || planForm.name.trim() === '') {
        toast.error('Plan name is required');
        return;
      }
      if (planForm.treatments.length === 0) {
        toast.error('At least one treatment is required');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare data for API
      // Validate treatments before mapping
      if (!planForm.treatments || planForm.treatments.length === 0) {
        toast.error('At least one treatment is required');
        return;
      }

      const treatmentsData = planForm.treatments.map((t, index) => {
        // Extract treatment ID - handle both object and string formats
        let treatmentId;
        if (t.treatment) {
          if (typeof t.treatment === 'object' && t.treatment._id) {
            treatmentId = t.treatment._id;
          } else if (typeof t.treatment === 'string') {
            treatmentId = t.treatment;
          } else if (t.treatment && typeof t.treatment === 'object' && t.treatment.toString) {
            // Mongoose ObjectId
            treatmentId = t.treatment.toString();
          } else {
            treatmentId = t.treatment;
          }
        } else if (t._id) {
          // Fallback for old format
          treatmentId = t._id;
        } else {
          console.error(`Invalid treatment entry at index ${index}:`, t);
          throw new Error(`Invalid treatment entry at index ${index}: missing treatment ID`);
        }

        // Ensure treatmentId is a valid string
        if (!treatmentId) {
          console.error(`Treatment ID is empty at index ${index}:`, t);
          throw new Error(`Treatment ID is empty at index ${index}`);
        }

        return {
          treatment: String(treatmentId),
          teeth: Array.isArray(t.teeth) ? t.teeth : [],
          quantity: Number(t.quantity) || 1,
          cost: Number(t.cost) || 0,
          discount: Math.min(100, Math.max(0, Number(t.discount) || 0))
        };
      });

      const planData = {
        patient: planForm.patientId,
        name: planForm.name.trim(),
        description: planForm.description || '',
        treatments: treatmentsData,
        discountPercent: Math.min(100, Math.max(0, Number(planForm.discountPercent) || 0)),
        totalCost: planForm.totalCost,
        estimatedDuration: planForm.estimatedDuration,
        status: planForm.status
      };

      if (selectedPlan) {
        // Update existing plan
        await axios.put(
          `${API_URL}/treatment-plans/${selectedPlan._id}`,
          planData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success(formatSuccessActionMessage('updated', 'Treatment plan'));
      } else {
        // Create new plan
        await axios.post(
          `${API_URL}/treatment-plans`,
          planData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success(formatSuccessActionMessage('created', 'Treatment plan'));
      }

      // Refresh the list
      await fetchTreatmentPlans();
      setPlanDialogOpen(false);
      setSelectedPlan(null);
      resetForm();
      
    } catch (err) {
      console.error('Error saving treatment plan:', err);
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('save', 'treatment plan'));
      toast.error(errorMessage);
    }
  };

  // Reset form
  const resetForm = () => {
    setPlanForm({
      patientId: '',
      name: '',
      description: '',
      treatments: [],
      totalCost: 0,
      estimatedDuration: 0,
      discountPercent: 0,
      status: 'draft'
    });
    setSelectedTreatmentForTeeth(null);
    setSelectedPlan(null);
  };

  // Handle dialog open
  const handlePlanDialogOpen = () => {
    setPlanDialogOpen(true);
  };

  // Handle dialog close
  const handlePlanDialogClose = () => {
    setPlanDialogOpen(false);
    resetForm();
    // Clear URL parameters to prevent dialog from reopening
    const patientId = searchParams.get('patientId');
    if (patientId) {
      searchParams.delete('patientId');
      setSearchParams(searchParams);
    }
  };

  // Handle view plan
  const handleViewPlan = async (plan) => {
    setSelectedPlan(plan);
    setViewDialogOpen(true);
    // Ensure currency is loaded (force refresh to get latest currency)
    const { getOrganizationCurrency } = await import('../../utils/currencyUtils');
    await getOrganizationCurrency(true); // Force refresh to get latest currency
  };

  // Handle edit plan
  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanForm({
      patientId: plan.patientId || plan.patient?._id || plan.patient,
      name: plan.name,
      description: plan.description,
      treatments: (plan.treatments || []).map(t => {
        const treatment = t.treatment || t;
        const quantity = Number(t.quantity) || 1;
        const cost = Number(t.cost) ?? (treatment?.price || t.price || 0) * quantity;
        const duration = Number(t.duration) || (treatment?.duration || t.duration || 0) * quantity;
        return {
          treatment,
          teeth: Array.isArray(t.teeth) ? t.teeth : [],
          quantity,
          discount: Math.min(100, Math.max(0, Number(t.discount) || 0)),
          cost,
          duration
        };
      }),
      totalCost: plan.totalCost,
      estimatedDuration: plan.estimatedDuration,
      discountPercent: Math.min(100, Math.max(0, Number(plan.discountPercent) || 0)),
      status: plan.status
    });
    setPlanDialogOpen(true);
  };

  // Handle delete plan
  const handleDeletePlan = (plan) => {
    setSelectedPlan(plan);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.delete(`${API_URL}/treatment-plans/${selectedPlan._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success(formatSuccessActionMessage('deleted', 'Treatment plan'));
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
      
      // Refresh the list
      await fetchTreatmentPlans();
      
    } catch (err) {
      console.error('Error deleting treatment plan:', err);
      const errorMessage = getApiErrorMessage(err, formatFailedActionMessage('delete', 'treatment plan'));
      toast.error(errorMessage);
    }
  };

  // Filter plans
  const filteredPlans = treatmentPlans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plan.patientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || plan.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Treatment Plans
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handlePlanDialogOpen}
        >
          Create Treatment Plan
        </Button>
      </Box>

      {/* Search and Filter */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Treatment Plans"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by plan name or patient..."
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchTreatmentPlans}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {treatmentPlans.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <MedicalServicesIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Treatment Plans Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Create your first treatment plan to start managing patient treatment sequences.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handlePlanDialogOpen}
            size="large"
          >
            Create First Treatment Plan
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredPlans.map((plan) => (
            <Grid item xs={12} md={6} lg={4} key={plan.id}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h2">
                      {plan.name}
                    </Typography>
                    <Chip
                      label={plan.status}
                      color={
                        plan.status === 'active' ? 'success' :
                        plan.status === 'completed' ? 'primary' :
                        plan.status === 'cancelled' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {plan.patientName}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <MedicalServicesIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {plan.treatments.length} treatments
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {plan.estimatedDuration} mins
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <MoneyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {formatCurrency(plan.totalCost)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewPlan(plan)}
                      color="primary"
                      title="View Plan"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) {
                            toast.error(SESSION_EXPIRED_MESSAGE);
                            return;
                          }
                          const response = await axios.get(`${API_URL}/treatment-plans/${plan._id || plan.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          await openTreatmentPlanPDF(response.data.data);
                        } catch (error) {
                          console.error('Error printing treatment plan:', error);
                          toast.error(formatFailedActionMessage('print', 'treatment plan'));
                        }
                      }}
                      color="primary"
                      title="Print Plan"
                    >
                      <PrintIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) {
                            toast.error(SESSION_EXPIRED_MESSAGE);
                            return;
                          }
                          const response = await axios.get(`${API_URL}/treatment-plans/${plan._id || plan.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          await downloadTreatmentPlanPDF(response.data.data);
                          toast.success(formatSuccessActionMessage('downloaded', 'Treatment plan'));
                        } catch (error) {
                          console.error('Error downloading treatment plan:', error);
                          toast.error(formatFailedActionMessage('download', 'treatment plan'));
                        }
                      }}
                      color="primary"
                      title="Download Plan"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditPlan(plan)}
                      color="primary"
                      title="Edit Plan"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeletePlan(plan)}
                      color="error"
                      title="Delete Plan"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Plan Dialog */}
      <Dialog
        open={planDialogOpen}
        onClose={handlePlanDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPlan ? 'Edit Treatment Plan' : 'Create Treatment Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Patient</InputLabel>
                <Select
                  name="patientId"
                  value={planForm.patientId}
                  onChange={handleFormChange}
                  label="Patient"
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient._id} value={patient._id}>
                      {patient.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Plan Name"
                name="name"
                value={planForm.name}
                onChange={handleFormChange}
                placeholder="e.g., Complete Dental Care Plan"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={planForm.description}
                onChange={handleFormChange}
                multiline
                rows={3}
                placeholder="Describe the treatment plan..."
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Selected Treatments
              </Typography>
              <List>
                {planForm.treatments.map((treatmentEntry, index) => {
                  const treatment = treatmentEntry.treatment || treatmentEntry;
                  const teeth = treatmentEntry.teeth || [];
                  const quantity = treatmentEntry.quantity || 1;
                  const cost = treatmentEntry.cost || treatment.price || 0;
                  const discount = treatmentEntry.discount ?? 0;
                  const treatmentId = treatment._id || treatmentEntry._id || index;
                  
                  return (
                    <ListItem key={treatmentId || index}>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body1" component="span">
                              {treatment.name}
                            </Typography>
                            {quantity > 1 && (
                              <Chip 
                                label={`×${quantity}`} 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              {formatCurrency(cost)} • {treatmentEntry.duration || treatment.duration || 0} mins
                              {discount > 0 && (
                                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                  ({discount}% off)
                                </Typography>
                              )}
                            </Typography>
                            {teeth.length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Teeth: {teeth.join(', ')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Discount %"
                        value={discount}
                        onChange={(e) => handleProcedureDiscountChange(index, e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        sx={{ width: 90, mr: 1 }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveTreatment(treatmentId)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
              {planForm.treatments.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No treatments selected
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Available Treatments
              </Typography>
              <Grid container spacing={2}>
                {treatments.map((treatment) => (
                  <Grid item xs={12} sm={6} md={4} key={treatment._id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2">{treatment.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {treatment.category}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(treatment.price)} • {treatment.duration} mins
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddTreatment(treatment)}
                          sx={{ mt: 1 }}
                        >
                          Add to Plan
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              {(() => {
                const { subtotal, planDiscountAmount, total } = getSubtotalAndTotal(planForm.treatments, planForm.discountPercent);
                return (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(subtotal)}</Typography>
                      {planForm.discountPercent > 0 && (
                        <>
                          <Typography variant="body2" sx={{ ml: 1 }}>Plan discount ({planForm.discountPercent}%):</Typography>
                          <Typography variant="body2" color="success.main" fontWeight={600}>-{formatCurrency(planDiscountAmount)}</Typography>
                        </>
                      )}
                      <TextField
                        size="small"
                        type="number"
                        label="Plan discount %"
                        value={planForm.discountPercent}
                        onChange={handlePlanDiscountChange}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="h6">
                        Total: {formatCurrency(planForm.totalCost)} • {planForm.estimatedDuration} mins
                      </Typography>
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="status"
                          value={planForm.status}
                          onChange={handleFormChange}
                          label="Status"
                        >
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                );
              })()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePlanDialogClose} type="button">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" type="button">
            {selectedPlan ? 'Update Plan' : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Plan Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedPlan && (
          <>
            <DialogTitle>
              Treatment Plan Details
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedPlan.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPlan.description}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Patient: {selectedPlan.patientName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Treatments
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Treatment</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Unit Price</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Total Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPlan.treatments.map((treatmentEntry, index) => {
                          const treatment = treatmentEntry.treatment || treatmentEntry;
                          const teeth = treatmentEntry.teeth || [];
                          const quantity = treatmentEntry.quantity || 1;
                          const totalCost = treatmentEntry.cost || treatment.price || 0;
                          // Calculate unit price
                          const unitPrice = quantity > 0 ? totalCost / quantity : totalCost;
                          const treatmentId = treatment._id || treatmentEntry._id || index;
                          // Get treatment name - handle both populated object and ID
                          const treatmentName = treatment?.name || treatment?.treatmentName || 'Unknown Treatment';
                          const treatmentCategory = treatment?.category || 'N/A';
                          const treatmentDuration = treatmentEntry.duration || treatment?.duration || 0;
                          
                          return (
                            <TableRow key={treatmentId}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {treatmentName}
                                </Typography>
                                {teeth.length > 0 && (
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Teeth: {teeth.join(', ')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{treatmentCategory}</TableCell>
                              <TableCell>{treatmentDuration} mins</TableCell>
                              <TableCell>{formatCurrency(unitPrice)}</TableCell>
                              <TableCell>
                                {quantity > 1 ? (
                                  <Chip label={`×${quantity}`} size="small" color="primary" />
                                ) : (
                                  '1'
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(totalCost)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="h6" component="span">
                      Total:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="h6" component="span" color="primary">
                        {formatCurrency(selectedPlan.totalCost || 0)}
                      </Typography>
                      <Typography variant="body1" component="span" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="h6" component="span">
                        {selectedPlan.estimatedDuration || 0} mins
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<PreviewIcon />}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      toast.error(SESSION_EXPIRED_MESSAGE);
                      return;
                    }
                    const response = await axios.get(`${API_URL}/treatment-plans/${selectedPlan._id || selectedPlan.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await openTreatmentPlanPDF(response.data.data);
                  } catch (error) {
                    console.error('Error previewing treatment plan:', error);
                    toast.error(formatFailedActionMessage('preview', 'treatment plan'));
                  }
                }}
              >
                Preview
              </Button>
              <Button
                startIcon={<PrintIcon />}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      toast.error(SESSION_EXPIRED_MESSAGE);
                      return;
                    }
                    const response = await axios.get(`${API_URL}/treatment-plans/${selectedPlan._id || selectedPlan.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await openTreatmentPlanPDF(response.data.data);
                  } catch (error) {
                    console.error('Error printing treatment plan:', error);
                    toast.error(formatFailedActionMessage('print', 'treatment plan'));
                  }
                }}
              >
                Print
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      toast.error(SESSION_EXPIRED_MESSAGE);
                      return;
                    }
                    const response = await axios.get(`${API_URL}/treatment-plans/${selectedPlan._id || selectedPlan.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    await downloadTreatmentPlanPDF(response.data.data);
                    toast.success(formatSuccessActionMessage('downloaded', 'Treatment plan'));
                  } catch (error) {
                    console.error('Error downloading treatment plan:', error);
                    toast.error(formatFailedActionMessage('download', 'treatment plan'));
                  }
                }}
              >
                Download
              </Button>
              <Button onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Delete Treatment Plan
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedPlan?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tooth Selector Dialog */}
      <ToothSelector
        open={toothSelectorOpen}
        onClose={() => {
          setToothSelectorOpen(false);
          setSelectedTreatmentForTeeth(null);
        }}
        onConfirm={handleTeethSelected}
        treatment={selectedTreatmentForTeeth}
        initialTeeth={[]}
      />
    </Container>
  );
};

export default TreatmentPlans; 